/**
 * Pure helpers for the EMR screenshot upload review: the operator drops
 * screenshots without picking patients, the agent identifies each one
 * (patient + which sections it shows), and the review groups them by patient
 * so the operator can fix what the agent couldn't before submitting.
 *
 * A screenshot "view" is the platform's per-screenshot state as FN returns it:
 * `{ screenshotId, identificationStatus, identificationIssue, taskId, patientId,
 *    patientName?, shownSections, extractionStatus, assignedBy }`.
 */

/** The data sections the agent needs from the EMR, in display order (the default site). */
export const EMR_SECTIONS = Object.freeze(["medications", "insurances"]);

/**
 * The sections a site that takes drug activity from the EMR treatment plan
 * (`agenticEmrActivityFromTreatmentPlan`) needs instead: the treatment plan
 * replaces the medication list.
 */
export const EMR_SECTIONS_FROM_TREATMENT_PLAN = Object.freeze(["treatmentPlans", "insurances"]);

/** The site's required EMR sections, from its `agenticEmrActivityFromTreatmentPlan` setting. */
export const emrSectionsFor = (activityFromTreatmentPlan) =>
  activityFromTreatmentPlan ? EMR_SECTIONS_FROM_TREATMENT_PLAN : EMR_SECTIONS;

/**
 * Everything a task can report as missing that has its own label (i18n
 * `agentic_platform.emr_tasks.section.*`): every site's required sections
 * (both `emrSectionsFor` outcomes, so no site's section is missed), plus the
 * treatment-plan details the agent couldn't read off a plan it did see.
 */
const LABELED_SECTIONS = new Set([
  ...EMR_SECTIONS,
  ...EMR_SECTIONS_FROM_TREATMENT_PLAN,
  "treatmentPlanDrugs",
  "treatmentPlanStatus"
]);

/**
 * The i18n key naming a section / missing category. A category this client
 * doesn't know yet (the agent learned a new one first) reads as a generic
 * "other EMR details" rather than a raw key.
 */
export const sectionLabelKey = (section) =>
  `agentic_platform.emr_tasks.section.${LABELED_SECTIONS.has(section) ? section : "other"}`;

/**
 * The label keys for a list of missing sections, deduped in order — several
 * categories this client doesn't know yet all read as the one "other" label,
 * which should be listed once.
 */
export const missingSectionLabelKeys = (missing) => [...new Set((missing || []).map(sectionLabelKey))];

export const IDENTIFICATION_STATUS = Object.freeze({
  PENDING: "pending",
  IDENTIFIED: "identified",
  UNIDENTIFIED: "unidentified",
  FAILED: "failed"
});

export const EMR_TASK_STATUS = Object.freeze({
  PENDING: "pending",
  NEEDS_SCREENSHOTS: "needs_screenshots"
});

const DISCARDED = "discarded";

/** Screenshots still waiting for the agent's first answer. */
export const isIdentifying = (view) => view?.identificationStatus === IDENTIFICATION_STATUS.PENDING;

/** Screenshots the operator removed from the upload. */
export const isDiscarded = (view) => view?.extractionStatus === DISCARDED;

/**
 * The reason key an unattributed screenshot is shown with (i18n
 * `agentic_platform.emr_tasks.issue.*`). A failed fast pass is not the
 * operator's fault — it reads as "still working on it", and they may assign it.
 */
export function issueKey(view) {
  if (view?.identificationStatus === IDENTIFICATION_STATUS.FAILED) return "identification_delayed";
  return view?.identificationIssue || "no_match";
}

/** Which of `sections` ANY of the given `shownSections` objects covers. */
export function unionSections(list, sections = EMR_SECTIONS) {
  const covered = Object.fromEntries(sections.map((section) => [section, false]));
  (list || []).forEach((shown) => {
    sections.forEach((section) => {
      if (shown?.[section]) covered[section] = true;
    });
  });
  return covered;
}

/** The sections (of `sections`) no screenshot has shown yet. */
export const missingSections = (shownSections, sections = EMR_SECTIONS) =>
  sections.filter((section) => !shownSections?.[section]);

/**
 * Split the review into what the operator sees:
 *   - `groups`     — one per identified patient (task), oldest screenshot first
 *   - `attention`  — unattributed screenshots, each with its reason
 *   - `identifying`— still waiting for the agent
 * Discarded screenshots are dropped. `tasksById` (the open tasks) contributes
 * each patient's label and the sections their EARLIER uploads already covered.
 * `sections` are the site's required sections (see `emrSectionsFor`).
 */
export function groupReview(views, tasksById = new Map(), sections = EMR_SECTIONS) {
  const groups = new Map();
  const attention = [];
  const identifying = [];

  (views || []).forEach((view) => {
    if (!view || isDiscarded(view)) return;
    if (isIdentifying(view)) {
      identifying.push(view);
      return;
    }
    if (view.identificationStatus === IDENTIFICATION_STATUS.IDENTIFIED && view.taskId != null) {
      const task = tasksById.get(view.taskId);
      const group = groups.get(view.taskId) || {
        taskId: view.taskId,
        patientId: view.patientId ?? task?.patientId ?? null,
        patientName: view.patientName ?? task?.patientName ?? null,
        task: task || null,
        screenshots: []
      };
      group.screenshots.push(view);
      groups.set(view.taskId, group);
      return;
    }
    attention.push(view);
  });

  const withCoverage = Array.from(groups.values()).map((group) => {
    const shownSections = unionSections(
      [...group.screenshots.map((view) => view.shownSections), group.task?.screenshots?.shownSections],
      sections
    );
    return { ...group, shownSections, missing: missingSections(shownSections, sections) };
  });

  return { groups: withCoverage, attention, identifying };
}

/**
 * The patient an unattributed screenshot most likely belongs to: when every
 * identified screenshot in the same review belongs to ONE patient (the common
 * "one patient, several screens" upload, where e.g. MedConnect's lower scroll
 * positions never show the title bar). Null when the review spans several
 * patients — then there is no safe guess.
 */
export function suggestedTaskId(groups) {
  return groups?.length === 1 ? groups[0].taskId : null;
}

/**
 * The status key of a task row in the queue (i18n
 * `agentic_platform.emr_tasks.task_status.*`):
 *   - back asking for more → `needs_<reason>`
 *   - has screenshots, all read → `ready`; some still being read → `reading`
 *   - nothing yet → `waiting`
 */
export function taskStatusKey(row) {
  if (row?.status === EMR_TASK_STATUS.NEEDS_SCREENSHOTS) {
    return `needs_${row.attentionReason || "partial_data"}`;
  }
  const summary = row?.screenshots;
  if (summary?.screenshots > 0) return summary.extracting > 0 ? "reading" : "ready";
  return "waiting";
}

/**
 * Sections still missing for a task row: what the agent reported when it sent
 * the task back, else what its attributed screenshots have not shown yet.
 * Empty for a task with no screenshots at all (nothing to say beyond "waiting").
 *
 * A sent-back task reports the agent's list as-is: the fast pass's
 * `shownSections` only says a section was on screen, not that the agent could
 * read it, so it can't clear what the agent found missing. The report can also
 * name treatment-plan details it couldn't read (`treatmentPlanDrugs`,
 * `treatmentPlanStatus`) — those likewise stay until the agent reads the task again.
 * `sections` are the site's required sections (see `emrSectionsFor`), used
 * for a task that wasn't sent back.
 */
export function taskMissingSections(row, sections = EMR_SECTIONS) {
  if (row?.status === EMR_TASK_STATUS.NEEDS_SCREENSHOTS && Array.isArray(row.missingCategories)) {
    return row.missingCategories;
  }
  if (!(row?.screenshots?.screenshots > 0)) return [];
  return missingSections(row.screenshots.shownSections, sections);
}

/**
 * The tasks this session's review attributed a screenshot to (uploaded or
 * assigned here, not yet submitted) — submitted and manually filled tasks'
 * screenshots leave the review, so what's left is new since then.
 */
export function reviewedTaskIds(views) {
  const ids = new Set();
  (views || []).forEach((view) => {
    if (isDiscarded(view)) return;
    if (view?.identificationStatus === IDENTIFICATION_STATUS.IDENTIFIED && view.taskId != null) ids.add(view.taskId);
  });
  return ids;
}

/**
 * Whether a queue row offers Submit. A waiting task needs at least one
 * attributed screenshot. A task the agent sent back already had its
 * screenshots submitted — resubmitting just those would loop straight back —
 * so it needs a screenshot this session's review attributed to it since
 * (`reviewedIds`, from `reviewedTaskIds`).
 */
export function canSubmitTask(row, reviewedIds = new Set()) {
  if (row?.status === EMR_TASK_STATUS.NEEDS_SCREENSHOTS) return reviewedIds.has(row.taskId);
  return row?.screenshots?.screenshots > 0;
}

/**
 * The `data:` URI for a screenshot as FN's PHI-gated endpoints return it
 * inline (`{ contentType, data }`, base64). Shared by every place that renders
 * one of these images (the upload review thumbnail, the read-only source
 * viewer) so they can't drift on the format.
 */
export function toImageDataUri(image) {
  return image ? `data:${image.contentType};base64,${image.data}` : null;
}

/**
 * Match the screenshots-upload response back to the staged items that produced
 * it, by original file name (in upload order — the same name can appear more
 * than once in one bulk). Pure so the matching can be tested without the
 * component's fetch/state plumbing; `handleUpload` keeps the orchestration
 * (calling the API, updating state, toasting the outcome).
 *
 * Returns:
 *  - `views`         — identification views to merge into the review
 *  - `previewsById`  — Map<screenshotId, previewUrl>, the local preview to carry
 *                       over for each uploaded file that came from this session
 *  - `retryItems`     — staged items that failed and should stay staged to retry
 *  - `droppedItems`   — staged items that are done with (neither uploaded nor
 *                       retryable) — their local preview can be revoked
 */
export function matchUploadResults(stagedItems, result) {
  const uploaded = result?.uploaded ?? [];
  const errors = result?.errors ?? [];

  const byName = new Map();
  (stagedItems || []).forEach((item) => {
    const queue = byName.get(item.file.name) || [];
    queue.push(item);
    byName.set(item.file.name, queue);
  });

  const previewsById = new Map();
  const views = uploaded.map((item) => {
    const staged = byName.get(item.originalName)?.shift();
    if (staged) previewsById.set(item.screenshotId, staged.previewUrl);
    return item.identification || { screenshotId: item.screenshotId, identificationStatus: "pending" };
  });

  // Whatever wasn't shifted out above didn't come back as an uploaded file —
  // keep it staged if it's a named failure, otherwise it's done with.
  const remainingStaged = Array.from(byName.values()).flat();
  const failedNames = new Set(errors.map((error) => error.originalName));
  const retryItems = remainingStaged.filter((item) => failedNames.has(item.file.name));
  const droppedItems = remainingStaged.filter((item) => !failedNames.has(item.file.name));

  return { views, previewsById, retryItems, droppedItems };
}

/**
 * Merge fresh views into the review map (keyed by screenshotId), newest wins —
 * except that a discarded screenshot stays discarded (a stale answer that
 * crossed the discard in flight can't bring it back).
 *
 * `onlyExisting` updates only screenshots still in the review, for answers to
 * a request made earlier (the identification poll): a screenshot that left
 * the review meanwhile (submitted, hidden) must not be re-added.
 */
export function mergeViews(current, fresh, { onlyExisting = false } = {}) {
  const next = new Map(current);
  (fresh || []).forEach((view) => {
    if (view?.screenshotId == null) return;
    const previous = next.get(view.screenshotId);
    if (isDiscarded(previous)) return;
    if (onlyExisting && !previous) return;
    next.set(view.screenshotId, { ...previous, ...view });
  });
  return next;
}
